/** @module csi/widget/_DndHTML5ListMixin */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom-class",
	"dojo/on",
	"underscore",
	"csi/dndHTML5",
	"csi/html"
], function(declare,lang,domClass,on,_,dndHTML5,html) {

	const DRAG_ROOT_ID = "__drag_root__";
	const DRAG_GROUP_ID = "dragGroupId";
	const DRAG_ITEM_ID = "dragItemId";
	const DRAG_DRAGGABLE = "draggable";
	const DRAG_NONE = 'none';
	const DRAG_COPY = 'copy';
	const DIVIDER_TOP = 2;
	const DIVIDER_LEFT = 4;

	html.registerDataAttribute("ATTR_LIST_MATRIX", "attr-list-matrix");
	html.registerDataAttribute("ATTR_LIST_GROUP", "attr-list-group");
	html.registerDataAttribute("DRAG_ROOT_ID", DRAG_ROOT_ID);
	html.registerDataAttribute("DRAG_ITEM_ID", DRAG_ITEM_ID);


	const DRAG_TYPE_FLAT = 1;
	const DRAG_TYPE_GROUP = 2;
	const DRAG_TYPE_GROUP_CHILD = 3;

	// Service functions to make code readable and self explanatory
	function isGroupElement(element) {
		return !!html.getDataAttribute(element, html.ATTR_LIST_GROUP);
	}
	function isGroupChildElement(element) {
		return element && !!(html.getDataAttribute(element, DRAG_GROUP_ID)?.toLowerCase() != DRAG_ROOT_ID);
	}
	function isMatrixElement(element) {
		return !!html.getDataAttribute(element, html.ATTR_LIST_MATRIX);
	}
	function isFlatElement(element) {
		return !!(html.getDataAttribute(element, DRAG_GROUP_ID)?.toLowerCase() === DRAG_ROOT_ID);
	}
	function isDraggableElement(element) {
		return !!element?.getAttribute(DRAG_DRAGGABLE);
	}

	return declare(null, {
		/** @private @property {boolean} _dndInitiated - indicate that drag and drop init() is already completed */
		_dndInitiated: false,
		
		/** @private @property {domNode} _dragElement - dom element which is currently being dragged */
		_dragElement: null,
		
		/** @private @property {domNode} _dragStartLocation - dom element next to original _dragElement location */
		_dragStartLocation: null,
		
		/** @private @property {boolean} _bDragGroup - if true it indicates that group being dragged */
		_bDragGroup: false,
		
		/** @private @property {boolean} bDragGroupFirst - if true dragged group can be placed at first position */
		bDragGroupFirst: false,

		/** @private @property {domNode} _lastTarget - last target element, used to avoid extra calculations */
		_lastTarget: null,

		/** @private @property {boolean} _bLeft - indicate whether drop location is left or right corner of the target */
		//_bLeft: false,

		/** @private @property {boolean} _bTop - indicate whether drop location is left or right corner of the target */
		//_bTop: false,

		/** @private @property {string} _dragOverCachedRespond - cached drag over respond */
		//_dragOverCachedRespond: ""

		/** @property {boolean} listDragActive - set to true when drag is in progress */
		listDragActive: false,

		/** @property {string} dropElementId - id of dragged element */
		dropElementId: "",

		/** @property {string} nextElementId - id of element to insert before */
		nextElementId: "",

		/** @property {string} targetGroup - new group id for dropped element */
		targetGroup: "",
		
		/**
		 * DnD initialization
		 */
		_dndInit: function() {
			if (!this._dndInitiated) {
				this._dndInitiated = true;
				this.on("dragstart", lang.hitch(this, this._dragStart));
				this.on("dragend", lang.hitch(this, this._dragEnd));
				this.on("dragover", lang.hitch(this, this._dragOver));
				this.on("drop", lang.hitch(this, this._dragDrop));
			}
		},
		
		/**
		 * Make Dom element draggable. This function should only be called once for each element. No own control.
		 * @param {domNode} div
		 * @param {boolean} bDraggable - some elements (img) are draggable by default, here you can make element not draggable
		 * @param {Sting} id - id of the element, can be use in callback to identify the item
		 * @param {Sting} dragId - id of the drag group. You can only drag-drop within same group 
		 */
		_setDraggable: function(div, bDraggable, id, dragId){
			if (bDraggable){
				div.setAttribute(DRAG_DRAGGABLE, true);
				html.setDataAttribute(div, DRAG_GROUP_ID, dragId || DRAG_ROOT_ID);
				html.setDataAttribute(div, DRAG_ITEM_ID, id);
				on(div, "mouseenter", lang.hitch(this, this._mouseEnter));
				on(div, "mouseleave", lang.hitch(this, this._mouseLeave));
			}
		},
		
		/**
		 * Process dragstart event, define drag flags, create drag image
		 * @param {DragEvent} event
		 */
		_dragStart: function(event){
			this.listDragActive = true;
			this.lastElement = null;
			this._dragOverCachedRespond = DRAG_NONE;

			const el = event.target;
			const nextSibling = el.nextSibling;
			domClass.add(this.domNode, "csi-list-drag-active");
			domClass.remove(el, "csi-drag-indicator");
			this._dragElement = el;
			this._dragStartLocation = nextSibling;
			
			// Create drag image if group was dragged, add all group's children to the image
			// Note: only visible elements (and members of the document body) will be visible on dragging avatar. 
			// If element is scrolled off screen and/or not visible - it will be not visible. If element is half visible - it will be half visible in avatar. 
			let div;
			if (isGroupElement(el)) {
				this._bDragGroup = true;
				div = html.create("div", {"class":"csi-drag-image"});
				div.appendChild(el.cloneNode(true));
				let child = nextSibling;
				const groupId = html.getDataAttribute(el, DRAG_ITEM_ID);
				while(child && (html.getDataAttribute(child, DRAG_GROUP_ID) === groupId)){
					div.appendChild(child.cloneNode(true));
					child = child.nextSibling;
				}
				this._dragStartLocation = child;
				el.appendChild(div);
				event.dataTransfer.setDragImage(div, 0, 0);
			}
			this._dragActive = true;
			// make it delayed to avoid affecting dragImage;
			_.delay(()=>{
				// don't change css if drag is already over
				if (this.listDragActive) {
					// Turn dragged element to placeholder 
					domClass.add(el, "csi-drag-placeholder");
					// hide grouped nodes being dragged
					let child = nextSibling;
					const groupId = html.getDataAttribute(el, DRAG_ITEM_ID);
					while(child && (html.getDataAttribute(child, DRAG_GROUP_ID) === groupId)){
						const theChild = child;
						child = child.nextSibling;
						this.nodesBeingDragged.appendChild(theChild);
					}
				}
				// Remove div used for drag image creation
				div?.remove();
			}, 1);
		},
		
		/**
		 * Drag End event. This event will happen regardless of the way how drag was ended: 
		 * with or without drop, in or outside of the container.... It always happens 
		 * @param {Event} event
		 */
		_dragEnd: function(event){
			domClass.remove(event.target, "csi-drag-placeholder");
			domClass.remove(this.domNode, "csi-list-drag-active");
			
			// If SelectList has check boxes, cb's input will be focused when user mouse down to start drag
			// Once drag starts, this cb will become hidden and lost focus, and so onBlur would close the drop down
			// To prevent this, we disable onBlur event on dropDown widget while dnd is in progress
			// Now when dnd ended, we have to restore focus so user can navigate away to close the drop down
			$(event.target).find("input").focus();
			
			this.listDragActive = false;
			if (this._dragActive){
				// Drag ended without valid drop. Return dragged element to original location
				const el = this._dragElement;
				el.parentNode.insertBefore(el, this._dragStartLocation);
				this._dragActive = false;
			}
			// we have to return dragged group nodes to correct position after dragElement
			let insertionPoint = this._dragElement;
			const {nodesBeingDragged} = this;
			const len = nodesBeingDragged?.children?.length

			for (let i=0; i<len; i++) {
				const node = nodesBeingDragged.children[0];
				insertionPoint.after(node);
				insertionPoint = node;
			}
			this._dragElement = null;
			this._dragStartLocation = null;
			this._bDragGroup = false;
		},
		
		/**
		 * Draw drag indicator 
		 * @param {Event} event
		 */
		_mouseEnter: function(event){
			if (!this._dragActive){
				domClass.add(event.target, "csi-drag-indicator");
			}
		},
		
		/**
		 * Remove drag indicator 
		 * @param {Event} event
		 */
		_mouseLeave: function(event){
			domClass.remove(event.target, "csi-drag-indicator");
		},

		/**
		 * Find draggable element in case if dragOver happen on one of the children elements
		 */
		_getTarget: function(event) {
			const jParent = $(event.target).parent("[draggable=true]");
			return jParent.length ? jParent[0] : event.target;
		},

		/**
		 * Calculate if placeholder position should be above or below current element
		 */
		_getBTop: function(event, target) {
			return event.offsetY < (target.offsetHeight / DIVIDER_TOP);
		},

		/**
		 * determine where user wants to drop dragged element: shift cursor left to place it to the root rather then inside the group (where possible)
		 */
		_isLeft: function(event) {
			return event.offsetX < (this._dragElement.offsetWidth / DIVIDER_LEFT);
		},

		/**
		 * return next and previous elements for calculations
		 */
		_getSiblings: function(target, bTop) {
			const {_dragElement} = this;
			let previousElement;
			let nextElement;
			if (_dragElement == target) {
				previousElement = target.previousSibling;
				nextElement = target.nextSibling;
			} else if (bTop) {
				previousElement = target.previousSibling;
				if (previousElement == _dragElement) {
					previousElement = previousElement?.previousSibling;
				}
				nextElement = target;
			} else {
				previousElement = target;
				nextElement = target.nextSibling;
				if (nextElement == _dragElement) {
					nextElement = nextElement?.nextSibling;
				}
			}
			return {previousElement, nextElement};
		},

		/**
		 * find out if element already exists in target group
		 */
		elementExists: function(dragElementId, targetGroup) {
			const targetGroupItems = this.store.query({parent: (targetGroup === html.DRAG_ROOT_ID ? null : targetGroup)});
			dragElementId = dragElementId.split("--")[0];
			const sameItemCount = targetGroupItems.filter((item)=>{
				return item.id.split("--")[0] === dragElementId;
			}).length;
			return sameItemCount > 0;
		},

		/**
		 * Process drag over
		 * Draw/move dragged element placeholder
		 * @param {Event} event
		 */
		_dragOver: function(event){
			dndHTML5.stopEvent(event);
			
			const target = this._getTarget(event);
			const bTop = this._getBTop(event, target);
			// Calculate if cursor over left part of the target
			let bLeft = this._isLeft(event);
			const bValidTarget = isDraggableElement(target);
			// check to see if disposition is changed - don't run the rest of the calculations otherwise
			if (!bValidTarget || ((this._lastTarget === target) && (this._bLeft === bLeft) && (this._bTop === bTop))) {
				event.dataTransfer.dropEffect = this._dragOverCachedRespond;
				return;
			}
			this._lastTarget = target;
			this._bTop = bTop;
			this._bLeft = bLeft;
			// previous and next elements properties
			const { previousElement, nextElement } = this._getSiblings(target, bTop);
			const bTopDrop = !isDraggableElement(previousElement); // drop before very first element
			const bPreviousElementGroup = isGroupElement(previousElement);
			const bPreviousElementGroupChild = isGroupChildElement(previousElement);
			const bPreviousElementMatrix = isMatrixElement(previousElement);
			// where drop happen: under group, inside group, to matrix group or it is no group (flat) drop
			const bUnderGroupDrop = !bTopDrop && ((bPreviousElementGroup || bPreviousElementGroupChild) && (!nextElement || isFlatElement(nextElement)));
			const bInsideGroupDrop = (bPreviousElementGroup || bPreviousElementGroupChild) && isGroupChildElement(nextElement);
			const bMatrixDrop = bPreviousElementMatrix && isMatrixElement(nextElement);
			const bFlatDrop = !bInsideGroupDrop && (bTopDrop || (isFlatElement(previousElement) && !bPreviousElementGroup));
			// dragged elements properties: are we dragging matrix? group? individual root item? individual group child?
			const dragElement = this._dragElement;
			const dragElementId = html.getDataAttribute(dragElement, DRAG_ITEM_ID);
			const dragElementGroup = html.getDataAttribute(dragElement, DRAG_GROUP_ID);
			const bMatrixDrag = isMatrixElement(dragElement);
			const bGroupDrag = !!this._bDragGroup;
			const bGroupChildDrag = isGroupChildElement(dragElement);
			// should we insert dropped to the root level?
			const bForceRoot = !(bGroupChildDrag && bMatrixDrag) && (bFlatDrop || bGroupDrag || (bUnderGroupDrop && (bLeft || bGroupDrag || bPreviousElementMatrix)));
			// what group dropped element should belong to?
			const previousElementGroupId = html.getDataAttribute(previousElement, DRAG_GROUP_ID);
			const nextElementGroupId = html.getDataAttribute(nextElement, DRAG_GROUP_ID);
			let targetGroup = bInsideGroupDrop ? nextElementGroupId : 
								bUnderGroupDrop ?
									(bForceRoot ? DRAG_ROOT_ID : 
										bPreviousElementGroup ? html.getDataAttribute(previousElement, DRAG_ITEM_ID) :
											 previousElementGroupId) : DRAG_ROOT_ID;
			// do not allow duplicate elements
			const elementExists = nextElement && !bGroupDrag && (targetGroup != dragElementGroup) && this.elementExists(dragElementId, targetGroup);
			if (elementExists) {
				event.dataTransfer.dropEffect = this._dragOverCachedRespond = DRAG_NONE;
				return;
			}
			// finally: can we drop here?
			const dragType = bGroupDrag ? DRAG_TYPE_GROUP : bGroupChildDrag ? DRAG_TYPE_GROUP_CHILD : DRAG_TYPE_FLAT;
			let canDrop = false;
			switch (dragType) {
				case DRAG_TYPE_FLAT: canDrop = !bMatrixDrop;
					break;
				case DRAG_TYPE_GROUP: canDrop = bTopDrop ? this.bDragGroupFirst : !bInsideGroupDrop || !nextElement;
					break;
				case DRAG_TYPE_GROUP_CHILD: canDrop = ((bMatrixDrop === bMatrixDrag) || (bUnderGroupDrop && bPreviousElementMatrix)) && 
														(bInsideGroupDrop || bUnderGroupDrop || bFlatDrop);
					break;
			}
			// only allow to drop matrix child into the same matrix
			if (canDrop && bMatrixDrop && bMatrixDrag) {
				canDrop = (dragElementGroup === targetGroup);
			}
/* kept this for now - will help fixing bugs
console.debug("Egor --- _dragOver", "dragType", dragType, "canDrop", canDrop, "bUnderGroupDrop", bUnderGroupDrop, "bForceRoot", bForceRoot, "bPreviousElementMatrix", bPreviousElementMatrix,
	"bInsideGroupDrop", bInsideGroupDrop, "bMatrixDrop", bMatrixDrop, "bFlatDrop", bFlatDrop, "bMatrixDrag", bMatrixDrag, "bValidTarget", bValidTarget);
*/
			// properly decorate dropped element according to it's new position and relocate it there; remember where we have it dropped to (group id)
			if (canDrop) {
				domClass.toggle(dragElement, "csi-drag-full-width", bForceRoot);
				// saving drop data to use with _onDragDrop() to update store in SelectListDnd module
				this.targetGroup = bForceRoot ? DRAG_ROOT_ID : targetGroup;
				this.nextElementId = nextElement && html.getDataAttribute(nextElement, DRAG_ITEM_ID);
				this.dragElementId = dragElementId;
				const dropDownContainer = target.parentNode;
				dropDownContainer.insertBefore(dragElement, nextElement);
			}

			// Update drag icon (showing if drag is acceptable or not)
			this._dragOverCachedRespond = event.dataTransfer.dropEffect = canDrop ? DRAG_COPY : DRAG_NONE;
		},
		
		/**
		 * Process on Drop event
		 * @param {Event} event
		 */
		_dragDrop: function(event){
			const {dragElementId, targetGroup, nextElementId, nodesBeingDragged} = this;
			this._dragActive = false;
			this._setTypeStyles();
			this._onDragDrop(dragElementId, targetGroup, nextElementId, nodesBeingDragged);
		},

		/**
		 * update dropped element css and group id data attribute
		 */
		_setTypeStyles: function() {
			const {_dragElement, targetGroup} = this;
			domClass.toggle(_dragElement.children[0], "csi-selectlist-child-checkbox", targetGroup != DRAG_ROOT_ID);
			html.setDataAttribute(_dragElement, DRAG_GROUP_ID, targetGroup);
		},
		
		/**
		 * DnD Drop extension point
		 * @param {String} dragElementId
		 * @param {String} targetGroup
		 * @param {String} nextElementId
		 * @param {domNode} nodesBeingDragged
		 */
		_onDragDrop: function(dragElementId, targetGroup, nextElementId, nodesBeingDragged){},
	});
});